import { Link } from "react-router-dom";
import { ArrowRight, Clock, FileText, Users, Download } from "lucide-react";
import { SignInButton, SignedOut } from "@clerk/clerk-react";
import { Section } from "@/layouts/Section";

const DIFFERENTIATORS = [
  {
    icon: FileText,
    title: "Ontario-aligned templates",
    desc: "Built directly from the latest editions of the Ontario Ministry of Education's Report Card formats. We stay up to date with new versions from the Ministry.",
  },
  {
    icon: Clock,
    title: "From hours to minutes",
    desc: "Teachers tell us they spend several hours per reporting period on comments alone. EasyReports brings that down to a fraction of the time.",
  },
  {
    icon: Users,
    title: "Class sharing & collaboration",
    desc: "Share a class with a co-teacher or occasional teacher and give them view or edit access - no shared logins required.",
  },
  {
    icon: Download,
    title: "Print-ready PDFs, instantly",
    desc: "Generate completed report card PDFs directly in the browser. No software, no formatting, no copy-pasting. Just download and submit.",
  },
];

export default function About() {
  return (
    <div className="about-page">

      {/* ── Intro ───────────────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[680px] mx-auto">
          <div className="section-label">About EasyReports</div>
          <h1 className="section-heading mb-5">
            Built for the teachers who do the most with the least time.
          </h1>
          <p className="text-[1.125rem] leading-[1.7] text-muted-foreground m-0 max-w-[580px]">
            Every Ontario elementary teacher spends hours writing individualized
            report card comments each term - while lesson planning, marking, and
            running a classroom. EasyReports was built to give that time back.
          </p>
        </div>
      </Section>

      {/* ── Mission ─────────────────────────────────────────────── */}
      <Section variant="muted">
        <div className="max-w-[1100px] mx-auto grid grid-cols-[1fr_340px] gap-16 items-start max-[860px]:grid-cols-1 max-[860px]:gap-12">
          <div className="flex flex-col gap-4">
            <div className="section-label">Our mission</div>
            <h2 className="section-heading mb-5">
              Educators should spend their energy on students, not paperwork.
            </h2>
            <p className="text-base leading-[1.7] text-muted-foreground m-0">
              By creating a platform that uses Ontario report card templates, allows 
              teachers to generate comments based on the information they already entered
              about that student. You can describe a student&apos;s strengths and
              next steps in your own language, we give you a  polished, student-tailored comment on the report card.
            </p>
            <p className="text-base leading-[1.7] text-muted-foreground m-0">
              We built this for the hard working Teachers of Ontario. Whether you have 20
              students or 30, the platform keeps everything organized in one place so you
              don&apos;t have to spend hours copying and pasting. It&apos;s easy to use
              and instantly generates the PDF export for you.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <div className="bg-card border border-border rounded-[14px] px-6 py-5">
              <div className="font-display text-[2.25rem] text-brand-700 leading-none mb-[0.3rem]">20+</div>
              <div className="text-[0.8125rem] text-muted-foreground leading-[1.4]">students in your homeroom class, every term</div>
            </div>
            <div className="bg-card border border-border rounded-[14px] px-6 py-5">
              <div className="font-display text-[2.25rem] text-brand-700 leading-none mb-[0.3rem]">40+</div>
              <div className="text-[0.8125rem] text-muted-foreground leading-[1.4]">students in other classes you teach as well</div>
            </div>
            <div className="bg-card border border-border rounded-[14px] px-6 py-5">
              <div className="font-display text-[2.25rem] text-brand-700 leading-none mb-[0.3rem]">~8hr</div>
              <div className="text-[0.8125rem] text-muted-foreground leading-[1.4]">avg. time teachers spend on just making comments</div>
            </div>
            <div className="bg-card border border-border rounded-[14px] px-6 py-5">
              <div className="font-display text-[2.25rem] text-brand-700 leading-none mb-[0.3rem]">&lt; 1hr</div>
              <div className="text-[0.8125rem] text-muted-foreground leading-[1.4]">with EasyReports</div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── Differentiators ─────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[1100px] mx-auto">
          <div className="section-label">What makes it different</div>
          <h2 className="section-heading">Designed for how Ontario teachers actually work.</h2>
          <div className="grid grid-cols-2 gap-6 max-[640px]:grid-cols-1">
            {DIFFERENTIATORS.map((d) => (
              <div key={d.title} className="bg-card border border-border rounded-[14px] p-7">
                <div className="w-10 h-10 rounded-[10px] bg-brand-700/10 text-brand-600 dark:bg-brand-400/15 dark:text-brand-300 flex items-center justify-center mb-4">
                  <d.icon size={20} />
                </div>
                <h3 className="text-[1.0625rem] font-semibold text-foreground m-0 mb-2">{d.title}</h3>
                <p className="text-[0.9rem] leading-relaxed text-muted-foreground m-0">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>


    </div>
  );
}
